/**
 * Región y moneda: dónde opera el negocio y en qué cobra.
 *
 * Pantalla propia y no un par de campos dentro de «Información del negocio»
 * porque no son dos datos más: de aquí cuelgan la moneda de TODOS los precios
 * —los del panel y los de Stripe—, cómo se escribe cada importe y qué plantilla
 * legal se ofrece. Un cambio con esa cola necesita sitio para avisar de lo que
 * va a pasar y para pedir confirmación, y eso no cabe entre el RFC y el
 * teléfono.
 *
 * ── Lo que la pantalla tiene que decir sí o sí ──────────────────────────────
 * 1. Que cambiar la moneda REETIQUETA los precios y no los convierte: 1 200 MXN
 *    pasan a ser 1 200 USD. Es la decisión que se tomó (ver
 *    `repriceOffersToCurrency`) y publicarla sin decirlo sería dejar a alguien
 *    vendiendo a un vigésimo de precio sin enterarse.
 * 2. Que las suscripciones vivas siguen cobrándose en la moneda con la que se
 *    firmaron. Lo decide Stripe, no nosotros, y quien tenga suscriptores tiene
 *    que saberlo ANTES de darle al botón.
 * 3. Que lo ya vendido no se toca. Un recibo no se reescribe.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCoin,
  IconLanguage,
  IconMapPin,
} from '@tabler/icons-react'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { useUnsavedGuard } from '../../../lib/unsavedChanges'
import { useToast } from '../../Toast'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { ConfirmDialog } from '../ConfirmDialog'
import { StripeAccountRows } from './StripeAccountRows'
import { StripeAttentionBanners } from './StripeAttentionBanners'
import { PROFILE_NOTE_KEY, useRegion } from '../../../lib/region'
import { useI18n } from '../../../lib/i18n'
import { countryLabel, currencyLabel } from '../../../lib/displayNames'
import {
  getProjectRegion,
  saveProjectRegion,
  type ProjectRegionSettings,
} from '../../../lib/api'
import { failureDetail } from './offerShared'

/** Un importe de ejemplo, para ver el formato antes de guardarlo. */
function preview(currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(1234.5)
  } catch {
    return `1234.50 ${currency.toUpperCase()}`
  }
}

export function RegionSection({ projectId, token, onBack, onOpenPolicies, onOpenOffers }: {
  projectId: string
  token: string
  onBack?: (() => void) | undefined
  onOpenPolicies?: (() => void) | undefined
  onOpenOffers?: (() => void) | undefined
}) {
  const { showToast } = useToast()
  const { t, language: panelLanguage } = useI18n()
  const { refresh } = useRegion()
  const [settings, setSettings] = useState<ProjectRegionSettings | null>(null)
  const [loading, setLoading] = useState(true)
  /* Sigue visible mientras se despide, aunque los datos ya hayan llegado. */
  const regionLoader = useRecordLoader(loading)
  const [failed, setFailed] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirming, setConfirming] = useState(false)

  // El borrador, con lo elegido pero todavía sin guardar.
  const [country, setCountry] = useState('')
  const [currency, setCurrency] = useState('')
  const [language, setLanguage] = useState<'es' | 'en'>('es')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getProjectRegion(token, projectId)
      setSettings(data)
      setCountry(data.countryCode)
      setCurrency(data.currency)
      setLanguage(data.language)
      setFailed('')
    } catch (error) {
      setFailed(failureDetail(error))
    } finally {
      setLoading(false)
    }
  }, [projectId, token])

  useEffect(() => { void load() }, [load])

  if (failed) {
    return (
      <Layout onBack={onBack}>
        <div className="dash-placeholder">
          <strong>{t('region.loadFailedTitle')}</strong>
          <span>{failed}</span>
          <button type="button" className="dash-secondary-btn" onClick={() => void load()}>{t('region.retry')}</button>
        </div>
      </Layout>
    )
  }

  /* Sigue visible mientras se despide, aunque el ajuste ya haya llegado. */
  if (regionLoader.visible || !settings) {
    return (
      <SectionLoader loader={regionLoader} label={t('region.loading')} />
    )
  }

  const chosenCountry = settings.countries.find((row) => row.code === country) ?? null
  const locale = chosenCountry?.locale ?? settings.locale
  const profile = chosenCountry?.profile ?? settings.profile
  const currencyChanged = currency !== settings.currency
  const dirty = currencyChanged || country !== settings.countryCode || language !== settings.language
  /* Sólo pide confirmación lo que arrastra algo: cambiar de país no reetiqueta
     nada, y un diálogo que salta siempre se aprende a cerrar sin leerlo — que
     es como se acaba confirmando el que sí importaba. */
  const needsConfirm = currencyChanged && settings.pricedOffers > 0

  const persist = async () => {
    setSaving(true)
    try {
      const result = await saveProjectRegion(token, projectId, {
        countryCode: country,
        currency,
        language,
      })
      await refresh()
      await load()
      setConfirming(false)

      if (result.repriced.failed > 0) {
        showToast(
          t('region.repricedPartial', { prices: result.repriced.prices, failed: result.repriced.failed }),
          'error',
        )
      } else if (result.repriced.prices > 0) {
        showToast(t('region.repriced', { prices: result.repriced.prices, offers: result.repriced.offers }))
      } else {
        showToast(t('region.saved'))
      }
    } catch (error) {
      showToast(failureDetail(error), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Layout onBack={onBack}>
      <div className="dash-account-grid">
        <StripeAttentionBanners projectId={projectId} token={token} />
        <section className="dash-settings-card is-raised">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconMapPin size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('region.jurisdiction.title')}</h3>
              <p>{t('region.jurisdiction.note')}</p>
            </div>
          </div>

          <label className="dash-field">
            <span>{t('region.country')}</span>
            <select
              value={country}
              onChange={(event) => {
                const code = event.target.value
                setCountry(code)
                /* Al cambiar de país se propone su moneda y su idioma, pero sólo
                   si no se habían tocado a mano: quien acaba de elegir dólares
                   no quiere que elegir México se los quite. */
                const next = settings.countries.find((row) => row.code === code)
                if (next && currency === settings.currency) setCurrency(next.currency)
                if (next && language === settings.language) setLanguage(next.language)
              }}
            >
              {settings.countries.map((row) => (
                <option key={row.code} value={row.code}>{countryLabel(row.code, panelLanguage, row.name)}</option>
              ))}
            </select>
            <p className="dash-field-hint">{t('region.profileNote', { note: t(PROFILE_NOTE_KEY[profile]) })}</p>
          </label>

          {onOpenPolicies ? (
            <p className="dash-field-hint">
              {t('region.policiesChangeBefore')} <button type="button" className="dash-inline-link" onClick={onOpenPolicies}>{t('region.policiesLink')}</button>.
            </p>
          ) : null}
        </section>

        <section className="dash-settings-card is-raised">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconCoin size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('region.currency.title')}</h3>
              <p>{t('region.currency.note')}</p>
            </div>
          </div>

          <label className="dash-field">
            <span>{t('region.currency.label')}</span>
            <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
              {settings.currencies.map((row) => (
                <option key={row.code} value={row.code}>{currencyLabel(row.code, panelLanguage, row.name)} · {row.code.toUpperCase()}</option>
              ))}
            </select>
            <p className="dash-field-hint">
              {t('region.currency.preview')} <strong>{preview(currency, locale)}</strong>
            </p>
          </label>

          {currencyChanged && settings.pricedOffers > 0 ? (
            <div className="dash-region-warning" role="status">
              <IconAlertTriangle size={16} aria-hidden="true" />
              <div>
                <strong>{settings.pricedOffers === 1
                  ? t('region.currency.warnOne')
                  : t('region.currency.warnMany', { count: settings.pricedOffers })}</strong>
                <p>
                  <strong>{t('region.currency.keepsAmountLead')}</strong>{' '}
                  {t('region.currency.keepsAmount', { currency: currency.toUpperCase() })}
                </p>
                {settings.stripeConnected ? (
                  <p>{t('region.currency.withStripe')}</p>
                ) : (
                  <p>{t('region.currency.withoutStripe', { currency: currency.toUpperCase() })}</p>
                )}
              </div>
            </div>
          ) : null}

          {settings.currenciesInUse.length > 1 ? (
            <p className="dash-field-hint">
              {t('region.currency.mixed', { codes: settings.currenciesInUse.map((code) => code.toUpperCase()).join(', ') })}
            </p>
          ) : null}
        </section>

        <section className="dash-settings-card is-raised">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconLanguage size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('region.language.title')}</h3>
              <p>{t('region.language.note')}</p>
            </div>
          </div>

          <label className="dash-field">
            <span>{t('region.language.label')}</span>
            <select value={language} onChange={(event) => setLanguage(event.target.value as 'es' | 'en')}>
              {/* Las mismas claves que el selector de idioma del panel: quien
                  elige lee «Español / Inglés» o «Spanish / English» según en
                  qué esté trabajando. Poner el endónimo fijo —«Español» siempre—
                  habría dado dos convenciones distintas para la misma pregunta
                  en dos pantallas del mismo producto. */}
              <option value="es">{t('language.es')}</option>
              <option value="en">{t('language.en')}</option>
            </select>
            <p className="dash-field-hint">
              {t('region.language.hint')}
            </p>
          </label>
        </section>

        {/* Va arriba, al lado de las notificaciones, como en el resto del panel.
            El portal lo coloca allí desde aquí: se pinta donde vive el estado
            que decide si hay cambios. */}
        <PendingChangesActions
          dirty={dirty}
          saving={saving}
          onDiscard={() => {
            setCountry(settings.countryCode)
            setCurrency(settings.currency)
            setLanguage(settings.language)
          }}
          onSave={() => { if (needsConfirm) setConfirming(true); else void persist() }}
        />

        {onOpenOffers ? (
          <p className="dash-field-hint">
            {t('region.checkOffersBefore')} <button type="button" className="dash-inline-link" onClick={onOpenOffers}>{t('section.payments')}</button>.
          </p>
        ) : null}

        {/* Al final: lo que de tu negocio guarda Stripe —el país y la moneda
            de la cuenta que cobra se deciden allí—. Ver `StripeAccountRows`. */}
        <StripeAccountRows projectId={projectId} token={token} />
      </div>

      {confirming ? (
        <ConfirmDialog
          title={t('region.confirm.title', { currency: currency.toUpperCase() })}
          confirmLabel={t('region.confirm.button')}
          confirmingLabel={t('region.confirm.working')}
          tone="danger"
          message={
            <>
              <p style={{ margin: '0 0 8px' }}>
                {settings.pricedOffers === 1
                  ? t('region.confirm.bodyOne', { stripe: settings.stripeConnected ? t('region.confirm.stripeTail') : '' })
                  : t('region.confirm.bodyMany', { count: settings.pricedOffers, stripe: settings.stripeConnected ? t('region.confirm.stripeTail') : '' })}
              </p>
              <p style={{ margin: 0 }}>
                <strong>{t('region.confirm.noConvertLead')}</strong>{' '}
                {t('region.confirm.noConvert', { currency: currency.toUpperCase() })}
              </p>
            </>
          }
          onConfirm={() => persist()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </Layout>
  )
}

function Layout({ onBack, children }: { onBack?: (() => void) | undefined; children: React.ReactNode }) {
  const { t } = useI18n()
  /* Volver con cambios sin guardar hace parpadear la barra de arriba en vez de
     perderlos. */
  const blockUnsaved = useUnsavedGuard()

  return (
    /* Entra desvanecido, no de golpe: la mascota acaba de irse desvaneciéndose
       y esto releva ese mismo gesto. */
    <div className="dash-dashboard-view dash-settings-view dash-region-settings dash-loaded-content-in">
      <div className="dash-settings-layout">
        <header className="dash-settings-intro">
          {onBack ? (
            <button type="button" className="dash-secondary-btn dash-settings-back" onClick={() => { if (!blockUnsaved()) onBack() }}>
              <IconArrowLeft size={14} /> {t('config.back')}
            </button>
          ) : null}
          <div className="dash-header" style={{ margin: 0 }}>
            <h1>{t('region.title')}</h1>
            <p>
              {t('region.intro')}
            </p>
          </div>
        </header>
        <div className="dash-settings-content">{children}</div>
      </div>
    </div>
  )
}
